import React from "react";

import { Grid, Tooltip } from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";
import { TFunction, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { TypographyButton } from "../../../../../components/SignUp/styled";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { Notification, NotificationUpdateAction } from "../../../../../models/notification";
import { User } from "../../../../../models/user";
import { NotificationAction } from "../../../../../util/enums";
import {
  NotificationsMenuItemRead,
  NotificationsMenuItemDescription,
  NotificationsMenuItemTitle,
  NotificationDate,
  FromUserImage,
  ImportantIcon,
  ImportantIconContainer,
  LoadingIcon,
} from "../../styles";
import NotificationBadge from "../NotificationBadge";
import { NotificationBadgeType } from "../type";
import { AppDynamicContent } from "Components/AppDynamicContent";

interface NotificationListItemProps {
  notification: Notification;
  notificationAction?: NotificationUpdateAction;
  isLoadingNotificationUpdate?: boolean;
  lastNotificationRef?: (notificationListItemComponent: HTMLDivElement) => void;
  changeMarkAsImportant: () => void;
  changeMarkAsRead: () => void;
  close: () => void;
  handleSelected: () => void;
}

const creatingNotificationDescription = (
  translate: TFunction<"translation", undefined>,
  notification: Notification,
  currentUser?: User
) => {
  const translateText = translate("by");
  const descriptionText = notification.description ? `${notification.description} ` : "";
  const userFromNotification = notification.fromUser?.name
    ? `${notification.fromUser.name} ${notification.fromUser.lastName ?? ""}`
    : `${currentUser?.company.name}`;
  return `${descriptionText}${translateText} <strong>${userFromNotification}</strong>`;
};

const NotificationListItem = ({
  notification,
  notificationAction,
  isLoadingNotificationUpdate,
  lastNotificationRef,
  changeMarkAsImportant,
  changeMarkAsRead,
  close,
  handleSelected,
}: NotificationListItemProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const history = useHistory();

  const getFromUserAvatar = (fromUser?: User): string => {
    if (fromUser && fromUser?.picture && fromUser?.picture.mediaLink) {
      return fromUser?.picture.mediaLink;
    }

    if (!fromUser && currentUser && currentUser?.picture && currentUser?.picture.mediaLink)
      return currentUser?.picture.mediaLink;

    // eslint-disable-next-line global-require
    return require("Assets/profile.png");
  };

  const handleOpenNotification = (notification: Notification) => {
    if (notification.resourceName && notification.resourceNumberId) {
      history.push(
        `${PRIVATE_ROUTES.NOTIFICATIONS}?type=${notification.resourceName}&id=${notification.resourceNumberId}`
      );
    } else {
      NotificationToast.error(translate("unavailableNotification"));
    }
  };

  const handleUpdateImportantNotification = (event: React.MouseEvent<SVGElement>) => {
    changeMarkAsImportant();
    event.stopPropagation();
  };

  return (
    <NotificationsMenuItemRead
      key={notification.id}
      ref={lastNotificationRef}
      container
      direction="row"
      isRead={notification.read}
      onClick={() => {
        handleOpenNotification(notification);
        handleSelected();
        close();
      }}
    >
      <Grid container item sm={1} direction="column">
        <FromUserImage src={getFromUserAvatar(notification.fromUser)} alt="from user avatar" />
      </Grid>
      <Grid container item sm={10} direction="column">
        <TypographyButton onClick={() => changeMarkAsRead()}>
          <div>
            <NotificationsMenuItemDescription>
              <AppDynamicContent
                htmlString={creatingNotificationDescription(translate, notification, currentUser)}
              />
            </NotificationsMenuItemDescription>
          </div>
          <Grid container>
            <NotificationsMenuItemTitle>{notification.title}</NotificationsMenuItemTitle>
            {notification.tags &&
              notification.tags.split(",").map((tag, index) => {
                if (index === 0) {
                  return (
                    <NotificationBadge
                      key={index}
                      value={tag}
                      badgeType={NotificationBadgeType.TAG}
                    />
                  );
                }

                return (
                  <NotificationBadge
                    key={index}
                    value={tag}
                    badgeType={NotificationBadgeType.DEFAULT}
                  />
                );
              })}
          </Grid>
          <NotificationDate>
            {notification.createdAt &&
              `${formatInTimeZone(
                new Date(notification.createdAt),
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                "dd/MM/yyyy"
              )} - ${formatInTimeZone(
                new Date(notification.createdAt),
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                "hh:mm a"
              )}`}
          </NotificationDate>
        </TypographyButton>
      </Grid>
      {notification.id && (
        <ImportantIconContainer container item sm={1} direction="column">
          {notificationAction?.ids?.some((notificationId) => notificationId === notification.id) &&
          notificationAction?.action === NotificationAction.IMPORTANT_FROM_DROPDOWN &&
          isLoadingNotificationUpdate ? (
            <LoadingIcon size={18} />
          ) : (
            <Tooltip
              title={
                notification.important
                  ? translate("unmarkAsImportant")
                  : translate("markAsImportant")
              }
              placement="top"
            >
              <div>
                <ImportantIcon
                  isImportant={notification.important}
                  onClick={(event) => handleUpdateImportantNotification(event)}
                />
              </div>
            </Tooltip>
          )}
        </ImportantIconContainer>
      )}
    </NotificationsMenuItemRead>
  );
};

export default React.memo(NotificationListItem);
